<script lang="ts">
    import type { Price, SaleTags } from '@shared/types/types';
    import BenefitTag from '@tile/components/benefit-tag/BenefitTag.svelte';
    import DealHighlight from '@tile/components/deal-highlight/DealHighlight.svelte';
    import Installments from '@tile/components/installments/Installments.svelte';
    import PriceRow from '@tile/components/price-row/PriceRow.svelte';
    import PriceContainer from '@tile/components/price/Price.svelte';
    import RetailPrice from '@tile/components/retail-price/RetailPrice.svelte';
    import SaleTag from '@tile/components/sale-tag/SaleTag.svelte';
    import SaleTagsComponent from '@tile/components/sale-tags/SaleTags.svelte';
    import SuggestedRetailPrice from '@tile/components/suggested-retail-price/SuggestedRetailPrice.svelte';
    import UnitPrice from '@tile/components/unit-price/UnitPrice.svelte';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import PricingItem from '@tilelist/layouts/experiments/e3892/pricing-item/PricingItem.svelte';

    interface Props {
        price: Price;
        saleTags?: SaleTags;
        dealHighlight?: string;
        dealId?: string;
        variationId: string;
        rule?: string;
        showInstallments?: boolean;
        bestVariationBenefit?: string;
    }

    let { price, saleTags, dealHighlight, dealId, variationId, rule, showInstallments, bestVariationBenefit }: Props =
        $props();

    const onex = injectWiring().onex;

    const effectiveSaleTags = $derived(saleTags);
    const showBenefitTag = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_BENEFITS'));
    const effectiveBestVariationBenefit = $derived(showBenefitTag ? bestVariationBenefit : undefined);
    const saleTagsEmpty = $derived(!effectiveSaleTags?.discount && !effectiveBestVariationBenefit);
    const showPricingComponent = $derived(!onex.isExperimentGroup('PRICING_COMPONENT', 'E3892A'));
</script>

{#snippet priceContent()}
    <DealHighlight {dealHighlight} />
    <UnitPrice unitPrice={price.normPrice} />
    {#if showInstallments}
        <Installments installmentPlan={price.installmentPlan} />
    {/if}
    <SaleTagsComponent isEmpty={saleTagsEmpty}>
        <SaleTag discount={effectiveSaleTags?.discount} />
        {#if showBenefitTag}
            <BenefitTag bestVariationBenefit={effectiveBestVariationBenefit} {rule} {variationId} />
        {/if}
    </SaleTagsComponent>
{/snippet}

{#if showPricingComponent}
    <PricingItem
        {price}
        deal={dealHighlight || dealId ? { highlight: dealHighlight, dealId } : undefined}
        {variationId}
        {rule}
        {showBenefitTag}
        bestVariationBenefit={effectiveBestVariationBenefit}
    />
{:else}
    <PriceContainer>
        <PriceRow>
            <RetailPrice {price} discounted={price.sale} />
            <SuggestedRetailPrice {price} />
        </PriceRow>
        {@render priceContent()}
    </PriceContainer>
{/if}
